// src/pages/ConfirmOrder.jsx

import React, { useEffect, useState } from 'react'
import FadeLoader from 'react-spinners/FadeLoader'
import axios from 'axios'
import { loadStripe } from '@stripe/stripe-js'
import errorImg from '../assets/error.png'
import successImg from '../assets/success.png'
import { stripe_sky, base_url } from '../utils/config'
import { useNavigate, useLocation, Link } from 'react-router-dom'

const load = () => loadStripe(stripe_sky)

export default function ConfirmOrder() {
  const [stripe, setStripe] = useState(null)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { state } = useLocation()

  // Pull orderId from URL, state or localStorage
  const urlParams = new URLSearchParams(window.location.search)
  let orderId =
    urlParams.get('orderId') ||
    state?.orderId ||
    localStorage.getItem('orderId')

  if (!orderId || orderId === 'null' || orderId === 'undefined') {
    console.error('⚠️ No valid orderId:', orderId)
    orderId = null
  } else {
    localStorage.setItem('orderId', orderId)
  }

  // Load Stripe.js
  useEffect(() => {
    load().then(setStripe)
  }, [])

  // Read Stripe payment status from client secret
  useEffect(() => {
    if (!stripe) return
    const clientSecret = urlParams.get('payment_intent_client_secret')
    if (!clientSecret) return

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => setMessage(paymentIntent.status))
      .catch(console.error)
  }, [stripe])

  // After a successful payment, mark order paid + request delivery
  const handleSuccess = async () => {
    if (!orderId) {
      setLoading(false)
      return
    }

    try {
      // 1) mark order as paid
      await axios.get(`${base_url}/api/order/confirm/${orderId}`)
      console.log('🔥 Order confirmed paid:', orderId)

      // 2) decide hyper vs standard
      const hasHyper = !!localStorage.getItem('oneDayQuotePrice')
      console.log('🔍 hasHyper flag:', hasHyper)

      if (hasHyper) {
        // Hyper-delivery (Borzo)
        const shippingInfo = JSON.parse(
          localStorage.getItem('shippingInfo') || '{}'
        )
        const quotePrice  = Number(localStorage.getItem('oneDayQuotePrice') || 0)
        const markupPrice = Number(localStorage.getItem('hyperDeliveryPrice') || 0)

        console.log('🚚 Hyper params:', {
          orderId,
          city: shippingInfo.city,
          area: shippingInfo.area,
          phone: `${shippingInfo.countryCode}${shippingInfo.phone}`,
          quotePrice,
          markupPrice
        })

        await axios.post(
          `${base_url}/api/borzo/request-hyper-delivery`,
          {
            orderId,
            city:       shippingInfo.city,
            area:       shippingInfo.area,
            phone:      `${shippingInfo.countryCode}${shippingInfo.phone}`,
            quotePrice,
            markupPrice
          }
        )
        console.log('✅ Hyper-delivery requested for', orderId)

      } else {
        // Standard delivery (Shiprocket)
        const courier_company_id = Number(
          localStorage.getItem('standardCourierCompanyId') || 0
        )
        const courier_name = localStorage.getItem('standardCourierName') || ''
        const price = Number(
          localStorage.getItem('standardDeliveryPrice') || 0
        )
        const transit_time =
          localStorage.getItem('standardTransitTime') || ''
        const estimated_delivery_days =
          localStorage.getItem('standardEstimatedDeliveryDays') || ''

        console.log('📦 Standard params:', {
          courier_company_id,
          courier_name,
          quotedPrice: price,
          transit_time,
          estimated_delivery_days
        })

        if (
          courier_company_id > 0 &&
          courier_name &&
          price > 0 &&
          transit_time &&
          estimated_delivery_days
        ) {
          await axios.post(
            `${base_url}/api/shiprocket/request-standard-delivery`,
            {
              orderId,
              courier_company_id,
              courier_name,
              quotedPrice: price,
              transit_time,
              estimated_delivery_days
            }
          )
          console.log('✅ Standard delivery requested for', orderId)
        } else {
          console.warn(
            '⚠️ Missing standard-delivery info; skipping Shiprocket request',
            { courier_company_id, courier_name, price, transit_time, estimated_delivery_days }
          )
        }
      }
    } catch (err) {
      console.error(
        'Error in delivery request flow →',
        err.response?.data || err
      )
    } finally {
      setLoading(false)
    }
  }

  const handleFailure = () => setLoading(false)

  useEffect(() => {
    if (message === 'succeeded') handleSuccess()
    else if (
      ['processing', 'requires_payment_method', 'canceled'].includes(message)
    ) {
      handleFailure()
    }
  }, [message])

  return (
    <div className="w-screen h-screen flex flex-col items-center justify-center gap-4">
      {message === 'succeeded' ? (
        loading ? (
          <FadeLoader />
        ) : (
          <>
            <img src={successImg} alt="success" className="w-16 h-16" />
            <div className="flex gap-4">
              <Link
                to="/dashboard/my-orders"
                className="px-5 py-2 bg-green-500 rounded-sm text-white"
              >
                Back to Dashboard
              </Link>
              <Link
                to="/dashboard/delivery-updates"
                className="px-5 py-2 bg-indigo-600 rounded-sm text-white"
              >
                Delivery Updates
              </Link>
            </div>
          </>
        )
      ) : ['processing', 'requires_payment_method', 'canceled'].includes(
          message
        ) ? (
        <>
          <img src={errorImg} alt="error" className="w-16 h-16" />
          <p className="text-red-500">Payment {message}. Please try again.</p>
          <Link
            to="/dashboard/my-orders"
            className="px-5 py-2 bg-green-500 rounded-sm text-white"
          >
            Back to Dashboard
          </Link>
        </>
      ) : (
        <FadeLoader />
      )}
    </div>
  )
}
